import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import {
  mtsDarkMode,
  setMtsDarkMode,
  clampMtsRange,
  mtsCanView,
  MTS_MAX_RANGE_DAYS,
} from "../mts-reporting-summary/mts-theme";

// See the summary page: the shared HTTP interceptor returns the raw error from
// its catchError instead of an observable, so on a failed request neither
// callback is ever called. Without this the page sits on "Loading…".
const REQUEST_TIMEOUT_MS = 30000;

interface Row {
  accountId: string;
  userName: string;
  operatorId: string;
  partnerId: string;
  currency: string;
  bets: number;
  totalStakes: number;
  totalReturn: number;
  pnl: number;
  margin: number;
  lastActivity: number;
}

interface Column {
  key: keyof Row;
  label: string;
  kind: "money" | "int" | "pct" | "stamp";
  signed?: boolean;
  hint?: string;
}

const COLUMNS: Column[] = [
  { key: "operatorId", label: "Operator", kind: "int" },
  { key: "partnerId", label: "Partner", kind: "int" },
  {
    key: "lastActivity",
    label: "Last Activity",
    kind: "stamp",
    hint:
      "When this account's counters were last written. Within one flush of its last bet, which is what the list is ordered on.",
  },
  { key: "bets", label: "Settled Bets", kind: "int" },
  {
    key: "totalStakes",
    label: "Total Stakes",
    kind: "money",
    hint: "What the account staked. Not turnover: a LAY risks its liability, which differs.",
  },
  {
    key: "totalReturn",
    label: "Total Return",
    kind: "money",
    hint: "What went back to the account - its stake plus its own net.",
  },
  {
    key: "pnl",
    label: "P&L",
    kind: "money",
    signed: true,
    hint: "The BOOK's, not the account's: positive means the house is up.",
  },
  {
    key: "margin",
    label: "Margin %",
    kind: "pct",
    signed: true,
    hint: "P&L over stakes.",
  },
];

@Component({
  selector: "app-mts-accounts",
  templateUrl: "./mts-accounts.component.html",
  styleUrls: ["./mts-accounts.component.scss"],
})
export class MtsAccountsComponent implements OnInit {
  readonly columns = COLUMNS;

  // SAPAdmin only, matching the API: the endpoint is in the list IsApplicable
  // grants to that one role, so any other role would be shown a page that can
  // only answer it with an authorisation error.
  canView = mtsCanView();
  // Whether the role has been read at all. See the template: a refusal shown
  // before anything has been checked is a guess, not an answer.
  checked = false;

  // UTC, and said so on the page. The user tier stores a UTC day on every row,
  // so this range is a run of those days - not a span of local hours that
  // something downstream has to reinterpret.
  selected: any = null;
  rangeNote = "";
  ranges: any = {
    Today: [moment.utc().startOf("day"), moment.utc().endOf("day")],
    Yesterday: [
      moment.utc().subtract(1, "days").startOf("day"),
      moment.utc().subtract(1, "days").endOf("day"),
    ],
    "Last 7 Days": [
      moment.utc().subtract(6, "days").startOf("day"),
      moment.utc().endOf("day"),
    ],
  };

  operatorIds: string[] = [];
  operatorData: any[] = [];
  selectedOperators: any[] = [];
  operatorSettings = {
    singleSelection: false,
    idField: "operator_id",
    textField: "operator_id",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    itemsShowLimit: 2,
    allowSearchFilter: true,
  };

  top: Row[] = [];
  bottom: Row[] = [];

  // The full list, paged. Separate from the two ranked panels on purpose: they
  // answer "who won and lost", this answers "who is here", and a reader
  // conflating them would take the first page for the biggest winners.
  list: Row[] = [];
  listLoading = false;
  listError = "";
  page = 1;
  readonly pageSize = 20;
  total = 0;
  // Recent by default - the first question a list of accounts answers is who
  // has been betting, which is how the console orders its own source list too.
  sortBy: "recent" | "stakes" | "pnl" = "recent";
  loading = false;
  loaded = false;
  errorMessage = "";

  // Shared with the other MTS pages, so moving between them does not change
  // the theme halfway through a report.
  dark = mtsDarkMode();

  constructor(private appService: AppService, private router: Router) {}

  ngOnInit() {
    // Re-read rather than trusting the field. The field is a snapshot taken
    // when the component was CONSTRUCTED, which on a cold load of this URL can
    // be before the session has been written - and a snapshot taken a moment
    // too early is indistinguishable, on screen, from not being permitted.
    this.canView = mtsCanView();
    this.checked = true;
    if (!this.canView) {
      return;
    }
    // Today, pre-filled but NOT run - the same reasoning as the Profitability
    // Report: a default is not a question anyone asked, and answering it costs
    // a query on every visit and presents figures that look chosen.
    this.selected = {
      startDate: moment.utc().startOf("day"),
      endDate: moment.utc().endOf("day"),
    };
    this.loadOperators();
  }

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
  }

  get isEmpty(): boolean {
    return this.loaded && !this.top.length && !this.bottom.length;
  }

  // The list is a filter control, so failing to fetch it must not stop the
  // report: it falls back to "all operators" and the page works.
  loadOperators() {
    const req = this.appService.getOperatorId();
    if (!req) {
      // getOperatorId returns undefined for roles that may not list operators,
      // and calling .subscribe on that would break the page.
      return;
    }
    req.subscribe(
      (res) => {
        if (!res || res.status !== "RS_OK" || !Array.isArray(res.operators)) {
          return;
        }
        this.operatorData = res.operators
          .filter((o: any) => o && o.operator_id && o.operator_id !== "all")
          .sort((a: any, b: any) => a.operator_id.localeCompare(b.operator_id));
      },
      () => {
        // Leave the list empty; the report itself is unaffected.
      }
    );
  }

  onDateChangeHandler(ev: any) {
    if (!ev || !ev.startDate || !ev.endDate) {
      return;
    }
    this.selected = clampMtsRange(ev.startDate, ev.endDate);
    this.rangeNote = this.selected.startDate.isSame(moment(ev.startDate), "day")
      ? ""
      : `Ranges are limited to ${MTS_MAX_RANGE_DAYS} days - showing the last ${MTS_MAX_RANGE_DAYS}.`;
  }

  // Collects the ids and stops there. Unlike the summary page this one does
  // not re-run on its own - the range can be a week of a busy book, so it waits
  // to be asked, the same as Apply.
  onOperatorChange() {
    this.operatorIds = (this.selectedOperators || [])
      .map((o) => o && o.operator_id)
      .filter((x) => !!x);
  }

  apply() {
    if (!this.selected || !this.selected.startDate) {
      this.errorMessage = "Pick a date range first.";
      return;
    }
    this.load();
  }

  // The range the page is CURRENTLY SHOWING, fixed at Apply.
  //
  // `selected` is the picker's live value and changes as soon as someone
  // touches the calendar. Paging and sorting call loadList() directly, so
  // reading `selected` there reloaded the list for a range the user had not
  // applied, while the two ranking panels above it still held the old one -
  // two halves of one screen over different periods, with nothing saying so.
  // The CSV had the same split: it exported the applied range under a filename
  // built from the picker.
  private appliedFrom = 0;
  private appliedTo = 0;

  load() {
    this.appliedFrom = Number(moment(this.selected.startDate).format("YYYYMMDD"));
    this.appliedTo = Number(moment(this.selected.endDate).format("YYYYMMDD"));
    this.page = 1;
    this.loadList();
    const seq = ++this.rankSeq;
    this.loading = true;
    this.loaded = true;
    this.errorMessage = "";

    // The day is read in UTC, because that is the calendar the rows are on.
    // Formatting it in local time would ask for a different day either side of
    // midnight, and the page would be a day out for half the world.
    const payload: any = {
      // moment(), NOT moment.utc(). The picker hands back a LOCAL midnight,
      // and converting that instant to UTC moves it backwards anywhere east
      // of Greenwich: in IST a pick of 10 Oct becomes 09 Oct, and a 7-day pick
      // becomes 8 days, which the server then rejects. The presets are already
      // UTC moments and format identically either way.
      //
      // The user tier is keyed on UTC days and the page says so - but a date
      // the analyst clicked is a CALENDAR DATE, not an instant to convert.
      fromDay: this.appliedFrom,
      toDay: this.appliedTo,
    };
    if (this.operatorIds.length) {
      payload.operatorIds = this.operatorIds;
    }

    this.appService
      .getMtsAccounts(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.rankSeq) {
            return; // a newer ranking load started
          }
          this.loading = false;
          if (!res || res.status !== "RS_OK") {
            this.top = [];
            this.bottom = [];
            this.errorMessage =
              (res && res.errorDescription) || "This report could not be loaded.";
            return;
          }
          // Server order, unmodified: it ranked them, and re-sorting here is
          // how the two ends end up disagreeing with the headings above them.
          this.top = res.top || [];
          this.bottom = res.bottom || [];
        },
        () => {
          if (seq !== this.rankSeq) {
            return;
          }
          this.loading = false;
          this.top = [];
          this.bottom = [];
          this.errorMessage = "This report could not be loaded.";
        }
      );
  }

  // Each load takes a ticket; only the newest may write to the page.
  //
  // Changing operator, date, sort or page fires a fresh request without
  // cancelling the one in flight, and these aggregations take seconds - so a
  // slow reply for the OLD filter can land after a fast one for the new and
  // leave the previous selection's rows under the current selection's heading.
  // Two counters, because the ranking and the list are independent requests.
  private rankSeq = 0;
  private listSeq = 0;

  // The page's own request. It carries the page number, so paging does not
  // re-run the two rankings - they do not change as you move through the list.
  loadList() {
    if (!this.selected || !this.selected.startDate) {
      return;
    }
    const seq = ++this.listSeq;
    this.listLoading = true;
    this.listError = "";

    const payload: any = {
      // The APPLIED range, not the picker's current value - see appliedFrom.
      fromDay: this.appliedFrom,
      toDay: this.appliedTo,
      page: this.page,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
    };
    if (this.operatorIds.length) {
      payload.operatorIds = this.operatorIds;
    }

    this.appService
      .getMtsAccountList(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.listSeq) {
            return; // a newer list load started
          }
          this.listLoading = false;
          if (!res || res.status !== "RS_OK") {
            this.list = [];
            this.total = 0;
            this.listError =
              (res && res.errorDescription) || "The account list could not be loaded.";
            return;
          }
          this.list = res.rows || [];
          this.total = res.total || 0;
        },
        () => {
          if (seq !== this.listSeq) {
            return;
          }
          this.listLoading = false;
          this.list = [];
          this.total = 0;
          this.listError = "The account list could not be loaded.";
        }
      );
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  // The page numbers to show: the ends, the current neighbourhood, and gaps
  // marked with 0 - so a hundred pages do not render as a hundred buttons.
  get pageNumbers(): number[] {
    const last = this.totalPages;
    const keep = new Set<number>([1, last, this.page - 1, this.page, this.page + 1]);
    const shown = Array.from(keep)
      .filter((n) => n >= 1 && n <= last)
      .sort((a, b) => a - b);
    const out: number[] = [];
    let prev = 0;
    for (const n of shown) {
      if (prev && n - prev > 1) {
        out.push(0);
      }
      out.push(n);
      prev = n;
    }
    return out;
  }

  goToPage(n: number) {
    if (n < 1 || n > this.totalPages || n === this.page) {
      return;
    }
    this.page = n;
    this.loadList();
  }

  setSort(by: "recent" | "stakes" | "pnl") {
    if (this.sortBy === by) {
      return;
    }
    this.sortBy = by;
    this.page = 1;
    this.loadList();
  }

  // An account opens its own page. The operator travels with it because an
  // account id is only unique inside its operator - an id alone would name two
  // different people and the detail page would show one of them at random.
  openAccount(row: Row) {
    if (!row || !row.accountId) {
      return;
    }
    this.router.navigate(["/mts/account", row.accountId], {
      queryParams: {
        operatorId: row.operatorId,
        partnerId: row.partnerId || null,
      },
    });
  }

  value(row: Row, col: Column): number {
    const v: any = row[col.key];
    return typeof v === "number" ? v : 0;
  }

  format(row: Row, col: Column): string {
    const raw: any = row[col.key];
    if (col.kind === "int" && typeof raw === "string") {
      return raw || "-";
    }
    if (col.kind === "stamp") {
      // Epoch millis. An account with no stored activity shows a dash rather
      // than 1970, which is what a raw zero formats to.
      // UTC, like every other date on this page. Local time here would put
      // the column 5.5 hours away from the range filter above it.
      return raw ? moment.utc(raw).format("DD MMM YYYY HH:mm") + " UTC" : "-";
    }
    const v = this.value(row, col);
    if (col.kind === "int") {
      return v.toLocaleString();
    }
    if (col.kind === "pct") {
      return `${v.toFixed(2)}%`;
    }
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  signClass(row: Row, col: Column): string {
    if (!col.signed) {
      return "";
    }
    const v = this.value(row, col);
    return v > 0 ? "pos" : v < 0 ? "neg" : "";
  }

  downloadCsv() {
    const esc = (s: any) => `"${String(s == null ? "" : s).replace(/"/g, '""')}"`;
    const head = ["Section", "Account", "Name", ...this.columns.map((c) => c.label)];
    const lines = [head.map(esc).join(",")];
    const push = (section: string, rows: Row[]) => {
      for (const r of rows) {
        lines.push(
          [section, r.accountId, r.userName || "", ...this.columns.map((c) => this.format(r, c))]
            .map(esc)
            .join(",")
        );
      }
    };
    push("Top", this.top);
    push("Bottom", this.bottom);

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    // The same calendar dates the request was sent with, so the filename
    // names the period the file actually covers.
    // The applied range: the period the rows in this file actually cover.
    a.download = `mts-accounts-${this.appliedFrom}-${this.appliedTo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
